import React from "react";
import { useNode, useEditor } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Divider
} from "@mui/material";

export const Image = ({ 
  src = "https://picsum.photos/400/300",
  isQAMode = false,
  questionImageSrc = "https://picsum.photos/400/300",
  answerImageSrc = "https://picsum.photos/400/300",
  mode = "question",
  frameWidth = "300px",
  aspectRatio = "4/3",
  objectFit = "contain",
  borderWidth = "1px",
  borderColor = "#ddd",
  borderRadius = "0px",
  pictureX = 0,
  pictureY = 0,
  pictureRotation = 0,
  pictureScale = 1,
  pictureFlipH = false,
  pictureFlipV = false,
  showBackground = true,
  backgroundColor = "#f9f9f9",
  hugImage = false,
  style,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));
  
  const formatSize = (value) => {
    if (!value) return 'auto';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  const formatRadius = (value) => {
    if (!value) return '0px';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  // Selection styling
  const getSelectionStyle = () => {
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const pictureTransform = `
    translate(${pictureX}px, ${pictureY}px)
    rotate(${pictureRotation}deg)
    scale(${pictureScale * (pictureFlipH ? -1 : 1)}, ${pictureScale * (pictureFlipV ? -1 : 1)})
  `;

  // Determine which image to show
  const getCurrentImageSrc = () => {
    if (isQAMode) {
      return mode === "question" ? questionImageSrc : answerImageSrc;
    }
    return src;
  };

  const handleModeChange = (newMode) => {
    if (newMode) {
      setProp(props => props.mode = newMode);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Mode Toggle - show when QA mode is enabled */}
      {isQAMode && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          marginBottom: 1,
          padding: 1,
          backgroundColor: '#f9f9f9',
          borderRadius: 1,
          border: '1px solid #ddd'
        }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, value) => handleModeChange(value)}
            size="small"
          >
            <ToggleButton value="question">Question</ToggleButton>
            <ToggleButton value="answer">Answer</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ fontSize: '12px', color: '#666', marginLeft: 1 }}>
            {mode === 'question' ? 'Showing question image' : 'Showing answer image'}
          </Box>
        </Box>
      )}

      <div 
        ref={ref => connect(drag(ref))}
        style={{ 
          width: formatSize(frameWidth),
          ...(hugImage ? {} : { aspectRatio: aspectRatio }),
          border: `${borderWidth} solid ${borderColor}`,
          borderRadius: formatRadius(borderRadius),
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: showBackground ? backgroundColor : 'transparent',
          transition: 'all 0.2s ease',
          ...style,
          ...getSelectionStyle()
        }}
        {...props}
      >
        <img 
          src={getCurrentImageSrc()}
          alt={isQAMode ? `${mode} image` : "Component image"}
          draggable={false}
          style={{ 
            width: '100%', 
            height: hugImage ? 'auto' : '100%', 
            objectFit: hugImage ? 'none' : objectFit,
            display: 'block',
            transform: pictureTransform,
            transformOrigin: 'center center',
            cursor: 'move'
          }}
        />
      </div>
    </div>
  );
};

const ImageSettings = () => {
  const { 
    actions: { setProp },
    src, isQAMode, questionImageSrc, answerImageSrc, mode,
    frameWidth, aspectRatio, objectFit,
    borderWidth, borderColor, borderRadius,
    pictureX, pictureY, pictureRotation, pictureScale,
    pictureFlipH, pictureFlipV, showBackground, backgroundColor, hugImage
  } = useNode((node) => ({
    src: node.data.props.src,
    isQAMode: node.data.props.isQAMode,
    questionImageSrc: node.data.props.questionImageSrc,
    answerImageSrc: node.data.props.answerImageSrc,
    mode: node.data.props.mode,
    frameWidth: node.data.props.frameWidth,
    aspectRatio: node.data.props.aspectRatio,
    objectFit: node.data.props.objectFit,
    borderWidth: node.data.props.borderWidth,
    borderColor: node.data.props.borderColor,
    borderRadius: node.data.props.borderRadius,
    pictureX: node.data.props.pictureX,
    pictureY: node.data.props.pictureY,
    pictureRotation: node.data.props.pictureRotation,
    pictureScale: node.data.props.pictureScale,
    pictureFlipH: node.data.props.pictureFlipH,
    pictureFlipV: node.data.props.pictureFlipV,
    showBackground: node.data.props.showBackground,
    backgroundColor: node.data.props.backgroundColor,
    hugImage: node.data.props.hugImage
  }));

  const [localSrc, setLocalSrc] = React.useState(src);
  const [localQuestionSrc, setLocalQuestionSrc] = React.useState(questionImageSrc);
  const [localAnswerSrc, setLocalAnswerSrc] = React.useState(answerImageSrc);
  const [localFrameWidth, setLocalFrameWidth] = React.useState(frameWidth);
  const [localBorderRadius, setLocalBorderRadius] = React.useState(borderRadius);
  const [localBorderWidth, setLocalBorderWidth] = React.useState(borderWidth);

  React.useEffect(() => setLocalSrc(src), [src]);
  React.useEffect(() => setLocalQuestionSrc(questionImageSrc), [questionImageSrc]);
  React.useEffect(() => setLocalAnswerSrc(answerImageSrc), [answerImageSrc]);
  React.useEffect(() => setLocalFrameWidth(frameWidth), [frameWidth]);
  React.useEffect(() => setLocalBorderRadius(borderRadius), [borderRadius]);
  React.useEffect(() => setLocalBorderWidth(borderWidth), [borderWidth]);

  const handleSizeChange = (prop, value) => {
    if (value && !value.includes('%') && !value.includes('px') && !value.includes('rem') && !value.includes('em')) {
      value = `${value}px`;
    }
    setProp(props => props[prop] = value);
  };

  return (
    <div>
      {/* QA Mode Toggle */}
      <FormControlLabel
        control={
          <Checkbox
            checked={isQAMode}
            onChange={(e) => setProp(props => props.isQAMode = e.target.checked)}
          />
        }
        label="Enable Question/Answer Mode"
      />

      {isQAMode && (
        <>
          <Box sx={{ padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1, marginY: 2 }}>
            <FormControl fullWidth margin="normal">
              <FormLabel>Current Mode</FormLabel>
              <Select
                value={mode || 'question'}
                onChange={(e) => setProp(props => props.mode = e.target.value)}
                size="small"
              >
                <MenuItem value="question">Question</MenuItem>
                <MenuItem value="answer">Answer</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>Question Image URL</FormLabel>
              <TextField
                value={localQuestionSrc}
                onChange={(e) => setLocalQuestionSrc(e.target.value)}
                onBlur={(e) => setProp(props => props.questionImageSrc = e.target.value)}
                size="small"
                placeholder="URL for question image"
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>Answer Image URL</FormLabel>
              <TextField
                value={localAnswerSrc}
                onChange={(e) => setLocalAnswerSrc(e.target.value)}
                onBlur={(e) => setProp(props => props.answerImageSrc = e.target.value)}
                size="small"
                placeholder="URL for answer image"
              />
            </FormControl>
          </Box>
          <Divider sx={{ marginY: 2 }} />
        </>
      )}

      {/* Regular Image Settings (only show when not in QA mode) */}
      {!isQAMode && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Image URL</FormLabel>
          <TextField
            value={localSrc}
            onChange={(e) => setLocalSrc(e.target.value)}
            onBlur={(e) => setProp(props => props.src = e.target.value)}
            size="small"
          />
        </FormControl>
      )}

      <FormControl fullWidth margin="normal">
        <FormLabel>Frame Width</FormLabel>
        <TextField
          value={localFrameWidth}
          onChange={(e) => setLocalFrameWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('frameWidth', e.target.value)}
          size="small"
          placeholder="300px, 100%"
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={hugImage}
            onChange={(e) => setProp(props => props.hugImage = e.target.checked)}
          />
        }
        label="Hug Image (Natural Ratio)"
      />

      {!hugImage && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Aspect Ratio</FormLabel>
          <Select
            value={aspectRatio || '4/3'}
            onChange={(e) => setProp(props => props.aspectRatio = e.target.value)}
            size="small"
          >
            <MenuItem value="1/1">Square (1:1)</MenuItem>
            <MenuItem value="4/3">Standard (4:3)</MenuItem>
            <MenuItem value="3/4">Portrait (3:4)</MenuItem>
            <MenuItem value="3/2">Classic (3:2)</MenuItem>
            <MenuItem value="2/3">Portrait Classic (2:3)</MenuItem>
            <MenuItem value="16/9">Widescreen (16:9)</MenuItem>
            <MenuItem value="9/16">Vertical (9:16)</MenuItem>
            <MenuItem value="5/4">Large Format (5:4)</MenuItem>
            <MenuItem value="4/5">Portrait Large (4:5)</MenuItem>
          </Select>
        </FormControl>
      )}

      {!hugImage && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Image Fit</FormLabel>
          <Select
            value={objectFit || 'contain'}
            onChange={(e) => setProp(props => props.objectFit = e.target.value)}
            size="small"
          >
            <MenuItem value="contain">Contain</MenuItem>
            <MenuItem value="cover">Cover</MenuItem>
            <MenuItem value="fill">Fill</MenuItem>
          </Select>
        </FormControl>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={showBackground}
            onChange={(e) => setProp(props => props.showBackground = e.target.checked)}
          />
        }
        label="Show Background"
      />

      {showBackground && (
        <FormControl fullWidth margin="normal" component="fieldset">
          <FormLabel component="legend">Background Color</FormLabel>
          <input
            type="color"
            value={backgroundColor || '#f9f9f9'}
            onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
            style={{ width: '100%', height: '40px', border: 'none', borderRadius: '4px' }}
          />
        </FormControl>
      )}

      <FormControl fullWidth margin="normal">
        <FormLabel>Border Width</FormLabel>
        <TextField
          value={localBorderWidth}
          onChange={(e) => setLocalBorderWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('borderWidth', e.target.value)}
          size="small"
          placeholder="0px, 1px, 2px, 4px"
        />
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Border Color</FormLabel>
        <input
          type="color"
          value={borderColor || '#dddddd'}
          onChange={(e) => setProp(props => props.borderColor = e.target.value)}
          style={{ width: '100%', height: '40px', border: 'none', borderRadius: '4px' }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Border Radius</FormLabel>
        <TextField
          value={localBorderRadius}
          onChange={(e) => setLocalBorderRadius(e.target.value)}
          onBlur={(e) => handleSizeChange('borderRadius', e.target.value)}
          size="small"
          placeholder="0px, 8px, 1rem"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Move X</FormLabel>
        <TextField
          value={pictureX || 0}
          onChange={(e) => setProp(props => props.pictureX = parseInt(e.target.value) || 0)}
          size="small"
          type="number"
          inputProps={{ step: 1 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Move Y</FormLabel>
        <TextField
          value={pictureY || 0}
          onChange={(e) => setProp(props => props.pictureY = parseInt(e.target.value) || 0)}
          size="small"
          type="number"
          inputProps={{ step: 1 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Rotation (degrees)</FormLabel>
        <TextField
          value={pictureRotation || 0}
          onChange={(e) => setProp(props => props.pictureRotation = parseInt(e.target.value) || 0)}
          size="small"
          type="number"
          inputProps={{ step: 5 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Scale</FormLabel>
        <TextField
          value={pictureScale || 1}
          onChange={(e) => setProp(props => props.pictureScale = parseFloat(e.target.value) || 1)}
          size="small"
          type="number"
          inputProps={{ step: 0.1 }}
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={pictureFlipH}
            onChange={(e) => setProp(props => props.pictureFlipH = e.target.checked)}
          />
        }
        label="Flip Horizontal"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={pictureFlipV}
            onChange={(e) => setProp(props => props.pictureFlipV = e.target.checked)}
          />
        }
        label="Flip Vertical"
      />
    </div>
  );
};

Image.craft = {
  props: {
    src: "https://picsum.photos/400/300",
    isQAMode: false,
    questionImageSrc: "https://picsum.photos/400/300",
    answerImageSrc: "https://picsum.photos/400/300", 
    mode: "question",
    frameWidth: "300px",
    aspectRatio: "4/3",
    objectFit: "contain",
    borderWidth: "1px",
    borderColor: "#ddd",
    borderRadius: "0px",
    pictureX: 0,
    pictureY: 0,
    pictureRotation: 0,
    pictureScale: 1,
    pictureFlipH: false,
    pictureFlipV: false,
    showBackground: true,
    backgroundColor: "#f9f9f9",
    hugImage: false
  },
  related: {
    settings: ImageSettings
  }
};